import { goalbookUrls } from '@/util/links'

const SSE_DATA_PREFIX = 'data: '
const MAX_LOGGED_EVENT_LENGTH = 200
// Grow ends its own runs at 180s, so a run silent for longer than this is a dead connection.
// Keepalive comments don't count, since a stalled run keeps sending them.
export const AGENT_SILENCE_TIMEOUT_MS = 200_000

export class AgentRequestError extends Error {
  readonly status: number
  readonly responseBody: unknown

  constructor(message: string, status: number, responseBody: unknown) {
    super(message)
    this.name = 'AgentRequestError'
    this.status = status
    this.responseBody = responseBody
  }
}

export interface AgentMessage {
  type: string
  data?: unknown
  error?: string
  message?: string
}

export interface AgentRunHandlers {
  onMessage: (message: AgentMessage) => void
  onError: (error: Error) => void
  onComplete: () => void
}

export interface AgentRun {
  finished: Promise<void>
  close: () => void
}

export async function createTask(formData: FormData): Promise<{ task_id: string }> {
  const response = await fetch(`${goalbookUrls.growApi}/toolkit-agent/agent/create`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  })
  if (!response.ok) {
    throw new AgentRequestError(`Failed to create task: HTTP ${response.status}`, response.status, await readBody(response))
  }
  return await response.json() as { task_id: string }
}

// Streams the agent's server-sent events. Each `data:` line is one JSON message; an `error`
// or `completed` message ends the run. Grow always sends one of the two, so a stream that closes
// without either died mid-run and is reported as an error rather than a finished search. After
// close() no handler is called again, so a cancelled search cannot deliver events into the one
// that replaced it.
export function runAgent(taskId: string, { onMessage, onError, onComplete }: AgentRunHandlers): AgentRun {
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  let closed = false
  let silenceTimer: ReturnType<typeof setTimeout> | undefined
  const abortRequest = new AbortController()

  const stop = () => {
    closed = true
    clearTimeout(silenceTimer)
    // the reader only exists once the response headers arrive, so only the abort
    // cancels a run closed while its request is still in flight
    abortRequest.abort()
    reader?.cancel().catch(() => {})
  }

  const failSilentRun = () => {
    onError(new Error('The search stopped responding'))
    onComplete()
    stop()
  }

  const restartSilenceTimer = () => {
    clearTimeout(silenceTimer)
    silenceTimer = setTimeout(failSilentRun, AGENT_SILENCE_TIMEOUT_MS)
  }

  const handleLine = (line: string): boolean => {
    if (closed || !line.startsWith(SSE_DATA_PREFIX)) {
      return closed
    }
    restartSilenceTimer()
    let message: AgentMessage
    try {
      message = JSON.parse(line.slice(SSE_DATA_PREFIX.length)) as AgentMessage
    } catch (e) {
      console.warn(`Skipping an unparseable agent event: ${line.slice(0, MAX_LOGGED_EVENT_LENGTH)}`, e)
      return false
    }
    if (message.type === 'error') {
      onError(new Error(agentErrorMessage(message)))
      onComplete()
      stop()
      return true
    }
    try {
      onMessage(message)
    } catch (e) {
      onError(e instanceof Error ? e : new Error(String(e)))
      stop()
      return true
    }
    if (message.type === 'completed') {
      onComplete()
      stop()
      return true
    }
    return false
  }

  const finished = (async () => {
    restartSilenceTimer()
    try {
      const response = await fetch(`${goalbookUrls.growApi}/toolkit-agent/agent/run/${taskId}`, { credentials: 'include', signal: abortRequest.signal })
      if (!response.ok || !response.body) {
        throw new AgentRequestError(`Failed to run agent: HTTP ${response.status}`, response.status, await readBody(response))
      }
      reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (!closed) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        if (lines.some(handleLine)) {
          return
        }
      }
      if (!closed && !handleLine(buffer.trim())) {
        onError(new Error('The search ended before it finished'))
        onComplete()
      }
    } catch (e) {
      if (closed) {
        return
      }
      onError(e instanceof Error ? e : new Error('Connection interrupted'))
      onComplete()
    } finally {
      clearTimeout(silenceTimer)
    }
  })()

  return { finished, close: stop }
}

function agentErrorMessage(message: AgentMessage): string {
  const data = message.data as { message?: string } | undefined
  return data?.message || message.error || message.message || JSON.stringify(message)
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text()
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}
